<template>
  <div data-testid="layout-settings">
    <EditorFormPanel v-model="layoutSettings" :title="getEditorTranslation('layout-group-label')">
      <div class="py-2">
        <EditorOptionsTabs
          v-model="textAlignModel"
          :legend="getEditorTranslation('text-align-label')"
          test-id-prefix="text-align"
          :options="textAlignOptions"
        />
      </div>

      <div class="py-2">
        <div class="flex justify-between mb-2">
          <UiFormLabel>{{ getEditorTranslation('background-color-label') }}</UiFormLabel>
        </div>
        <EditorColorPicker v-model="navigationBlock.color.backgroundColor" class="w-full">
          <template #trigger="{ color, toggle }">
            <label>
              <SfInput v-model="navigationBlock.color.backgroundColor" type="text" data-testid="input-background-color">
                <template #suffix>
                  <button
                    type="button"
                    class="border border-[#a0a0a0] rounded-lg cursor-pointer w-10 h-8"
                    :style="{ backgroundColor: color }"
                    :aria-label="getEditorTranslation('open-background-color-picker-label')"
                    @mousedown.stop
                    @click.stop="toggle"
                  />
                </template>
              </SfInput>
            </label>
          </template>
        </EditorColorPicker>
      </div>

      <div class="py-2">
        <div class="flex justify-between mb-2">
          <UiFormLabel>{{ getEditorTranslation('text-color-label') }}</UiFormLabel>
        </div>
        <EditorColorPicker v-model="navigationBlock.color.textColor" class="w-full">
          <template #trigger="{ color, toggle }">
            <label>
              <SfInput v-model="navigationBlock.color.textColor" type="text" data-testid="input-text-color">
                <template #suffix>
                  <button
                    type="button"
                    class="border border-[#a0a0a0] rounded-lg cursor-pointer w-10 h-8"
                    :style="{ backgroundColor: color }"
                    :aria-label="getEditorTranslation('open-text-color-picker-label')"
                    @mousedown.stop
                    @click.stop="toggle"
                  />
                </template>
              </SfInput>
            </label>
          </template>
        </EditorColorPicker>
      </div>

      <div class="py-2">
        <div class="flex justify-between mb-2">
          <UiFormLabel>{{ getEditorTranslation('hover-background-color-label') }}</UiFormLabel>
        </div>
        <EditorColorPicker v-model="navigationBlock.color.hoverBackgroundColor" class="w-full">
          <template #trigger="{ color, toggle }">
            <label>
              <SfInput
                v-model="navigationBlock.color.hoverBackgroundColor"
                type="text"
                data-testid="input-hover-background-color"
              >
                <template #suffix>
                  <button
                    type="button"
                    class="border border-[#a0a0a0] rounded-lg cursor-pointer w-10 h-8"
                    :style="{ backgroundColor: color }"
                    :aria-label="getEditorTranslation('open-hover-background-color-picker-label')"
                    @mousedown.stop
                    @click.stop="toggle"
                  />
                </template>
              </SfInput>
            </label>
          </template>
        </EditorColorPicker>
      </div>

      <div class="py-2">
        <UiFormLabel>{{ getEditorTranslation('padding-label') }}</UiFormLabel>
        <div class="grid grid-cols-4 gap-px rounded-md overflow-hidden border border-gray-300">
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowUpward /></span>
            <input
              v-model.number="navigationBlock.layout.paddingTop"
              type="number"
              class="w-8 text-center outline-none"
              data-testid="padding-top"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowDownward /></span>
            <input
              v-model.number="navigationBlock.layout.paddingBottom"
              type="number"
              class="w-8 text-center outline-none"
              data-testid="padding-bottom"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowBack /></span>
            <input
              v-model.number="navigationBlock.layout.paddingLeft"
              type="number"
              class="w-8 text-center outline-none"
              data-testid="padding-left"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white">
            <span><SfIconArrowForward /></span>
            <input
              v-model.number="navigationBlock.layout.paddingRight"
              type="number"
              class="w-8 text-center outline-none"
              data-testid="padding-right"
            />
          </div>
        </div>
      </div>
    </EditorFormPanel>
  </div>
</template>

<script setup lang="ts">
import {
  SfIconArrowUpward,
  SfIconArrowDownward,
  SfIconArrowBack,
  SfIconArrowForward,
  SfInput,
} from '@storefront-ui/vue';

import type { NavigationFormProps, NavigationContent } from './types';
const props = defineProps<NavigationFormProps>();
const layoutSettings = ref(true);

const { allBlocks } = useBlocks();

const { blockUuid } = useSiteConfiguration();
const { findOrDeleteBlockByUuid } = useBlockManager();

const navigationBlock = computed<NavigationContent>(() => {
  const rawContent = findOrDeleteBlockByUuid(allBlocks.value, props.uuid || blockUuid.value)?.content ?? {};
  const content = rawContent as Partial<NavigationContent>;

  if (!content.layout) content.layout = {};
  content.layout.paddingTop = content.layout.paddingTop ?? 0;
  content.layout.paddingBottom = content.layout.paddingBottom ?? 0;
  content.layout.paddingLeft = content.layout.paddingLeft ?? 0;
  content.layout.paddingRight = content.layout.paddingRight ?? 0;

  if (!content.text) content.text = {};
  content.text.textAlignment = content.text.textAlignment ?? 'left';

  if (!content.color) content.color = {};
  content.color.backgroundColor = content.color.backgroundColor ?? '';
  content.color.textColor = content.color.textColor ?? '';
  content.color.hoverBackgroundColor = content.color.hoverBackgroundColor ?? '';

  return content as NavigationContent;
});
const { textAlignModel, textAlignOptions } = useEditorOptionsTabs(() => navigationBlock.value, getEditorTranslation);
</script>

<i18n lang="json">
{
  "en": {
    "text-color-label": "Text Color",
    "text-align-label": "Text alignment",
    "text-align-option-left-label": "Left",
    "text-align-option-center-label": "Center",
    "text-align-option-right-label": "Right",

    "layout-group-label": "Layout",
    "background-color-label": "Background Color",
    "hover-background-color-label": "Link Hover Color",
    "padding-label": "Padding",
    "open-background-color-picker-label": "Open background color picker",
    "open-text-color-picker-label": "Open text color picker",
    "open-hover-background-color-picker-label": "Open hover background color picker"
  },
  "de": {
    "text-color-label": "Text Color",
    "text-align-label": "Text alignment",
    "text-align-option-left-label": "Left",
    "text-align-option-center-label": "Center",
    "text-align-option-right-label": "Right",

    "layout-group-label": "Layout",
    "background-color-label": "Background Color",
    "hover-background-color-label": "Link Hover Color",
    "padding-label": "Padding",
    "open-background-color-picker-label": "Open background color picker",
    "open-text-color-picker-label": "Open text color picker",
    "open-hover-background-color-picker-label": "Open hover background color picker"
  }
}
</i18n>
